import { CommonModule } from '@angular/common';
import { Location } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import Character from '../../../models/Game Layer/character';
import Session from '../../../models/Game Layer/session';
import { GamelayerService } from '../../../services/gamelayer-service';
import { LoadingComponent } from '../../loading-component/loading-component';
import { BackButtonComponent } from '../../back-button-component/back-button-component';

@Component({
  selector: 'app-character-info-component',
  standalone: true,
  imports: [CommonModule, LoadingComponent, BackButtonComponent],
  templateUrl: './character-info-component.html',
  styleUrl: './character-info-component.css',
})
export class CharacterInfoComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly gamelayerService = inject(GamelayerService);

  readonly character = signal(new Character());
  readonly session = signal<Session | null>(null);

  readonly isLoadingCharacter = signal(false);
  readonly loadCharacterError = signal('');
  readonly nonBondStats = computed(() =>
    this.character().stats.filter((stat) => stat.name !== 'Bond'),
  );
  readonly bondStat = computed(() =>
    this.character().stats.find((stat) => stat.name === 'Bond'),
  );
  readonly hp = computed(() => this.calculateHP(this.character()));

  ngOnInit(): void {
    const sessionId = this.route.snapshot.paramMap.get('sessionId');
    const characterId = this.route.snapshot.paramMap.get('characterId');

    if (sessionId && characterId) {
      this.loadCharacter(sessionId, characterId);
    }
  }

  getConstitutionModifier(character: Character): number {
    const conStat = character.stats.find((stat) => stat.name === 'Constitution');
    if (!conStat) {
      return 0;
    }

    return Math.floor(conStat.value);
  }

  calculateHP(character: Character): number {
    if (character.type === 'Rider') {
      return 10 + this.getConstitutionModifier(character);
    }

    return 0;
  }

  getHPArray(): number[] {
    const maxHP = this.calculateHP(this.character());
    return Array.from({ length: maxHP }, (_, i) => i + 1);
  }

  goBack(): void {
    this.location.back();
  }

  private loadCharacter(sessionId: string, characterId: string): void {
    this.isLoadingCharacter.set(true);
    this.loadCharacterError.set('');

    this.gamelayerService.getCharacterById(sessionId, characterId).subscribe({
      next: (response: Character | { character?: Character }) => {
        console.log(response);

        const characterData =
          (response as { character?: Character })?.character ?? response;

        this.character.set(new Character(characterData));
        this.isLoadingCharacter.set(false);
        this.loadSession(sessionId);
      },
      error: (error) => {
        console.error('Error loading character:', error);
        this.loadCharacterError.set('The character sheet could not be opened right now.');
        this.isLoadingCharacter.set(false);
      },
    });
  }

  private loadSession(sessionId: string): void {
    this.gamelayerService.getSessionById(sessionId).subscribe({
      next: (response) => {
        this.session.set(new Session(response));
      },
      error: (error) => {
        console.error('Error loading character session:', error);
        this.session.set(null);
      },
    });
  }
}
